import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { companyInfo, history } from '@/mocks/companyData';

export default function Company() {
  const [activeTab, setActiveTab] = useState<'overview' | 'history'>('overview');

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-medium tracking-[0.15em] text-primary-600 bg-primary-100/70 px-4 py-1.5 rounded-full mb-6">
              COMPANY
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-6 tracking-wide">
              会社概要
            </h1>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-2xl">
              3D's Card Dataは、データ解析技術を通じて企業の未来を創造するテクノロジーカンパニーです。
              私たちはデータの力でビジネスの可能性を最大限に引き出します。
            </p>
          </div>
        </div>
      </section>

      {/* Tab Switcher */}
      <section className="bg-background-100 border-b border-background-200 sticky top-[72px] z-30">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-1 py-1">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeTab === 'overview'
                  ? 'bg-foreground-900 text-background-50'
                  : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              企業情報
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeTab === 'history'
                  ? 'bg-foreground-900 text-background-50'
                  : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              沿革
            </button>
          </div>
        </div>
      </section>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Company Info Grid */}
          <section className="py-16 md:py-20">
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16">
                {/* Left: Image */}
                <div className="relative">
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Roppongi%20Hills%20Mori%20Tower%20Tokyo%20modern%20skyscraper%20with%20warm%20sunset%20lighting%20reflection%20on%20glass%20facade%20elegant%20corporate%20atmosphere%20minimalist%20Japanese%20aesthetic%20professional%20office%20building%20luxury%20feel&width=800&height=600&seq=company-building-roppongi&orientation=landscape"
                      alt="六本木ヒルズ森タワー"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Right: Info Table */}
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 tracking-wide">
                    企業情報
                  </h2>
                  <dl className="divide-y divide-background-200">
                    {[
                      { label: '会社名', value: companyInfo.name },
                      { label: '英文社名', value: companyInfo.nameEn },
                      { label: '設立', value: companyInfo.founded },
                      { label: '資本金', value: companyInfo.capital },
                      { label: '代表取締役', value: `${companyInfo.representative}（${companyInfo.representativeTitle}）` },
                      { label: '従業員数', value: companyInfo.employees },
                      { label: '所在地', value: companyInfo.address },
                      { label: 'TEL', value: companyInfo.tel },
                      { label: 'FAX', value: companyInfo.fax },
                      { label: 'E-mail', value: companyInfo.email },
                      { label: '事業内容', value: companyInfo.business },
                    ].map((item) => (
                      <div key={item.label} className="flex flex-col sm:flex-row py-4 gap-1 sm:gap-6">
                        <dt className="text-xs font-medium text-foreground-400 tracking-wider sm:w-28 shrink-0">
                          {item.label}
                        </dt>
                        <dd className="text-sm text-foreground-800 leading-relaxed">
                          {item.label === 'E-mail' ? (
                            <a href={`mailto:${item.value}`} className="text-primary-600 hover:text-primary-700 transition-colors">
                              {item.value}
                            </a>
                          ) : (
                            item.value
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
            </div>
          </section>

          {/* CEO Message */}
          <section className="py-16 md:py-20 bg-background-100">
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 items-center">
                <div>
                  <span className="inline-block text-xs font-medium tracking-[0.15em] text-accent-600 bg-accent-100/70 px-4 py-1.5 rounded-full mb-6">
                    MESSAGE
                  </span>
                  <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 leading-snug mb-6 tracking-wide">
                    代表メッセージ
                  </h2>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    データは21世紀の最も重要な資産です。しかし、その価値を最大限に引き出せている企業はまだ少数です。3D's Card Dataは、独自のAI技術とデータ解析エンジンを通じて、あらゆる企業がデータの真の価値を発見し、ビジネスの成長につなげられる世界を目指しています。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    私たちは「正確さ」「速さ」「わかりやすさ」の3つをモットーに、お客様のデータ活用を包括的にサポートします。3Dカードデータ解析という独自の技術を核に、これからも革新を続けてまいります。
                  </p>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                    データドリブンな未来を、共に創造しましょう。
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">{companyInfo.representative}</p>
                    <p className="text-xs text-foreground-500">{companyInfo.representativeTitle} / 3D's Card Data株式会社</p>
                  </div>
                </div>
                <div className="relative">
                  <div className="aspect-[3/4] rounded-2xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Professional%20Japanese%20businessman%20CEO%20portrait%20in%20modern%20office%20warm%20lighting%20confident%20expression%20wearing%20suit%20no%20tie%20minimalist%20background%20elegant%20corporate%20photography%20soft%20shadows%20depth%20of%20field&width=600&height=800&seq=ceo-portrait-japanese&orientation=portrait"
                      alt="代表取締役 佐藤健一"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Access */}
          <section className="py-16 md:py-20">
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
              <div className="text-center mb-12">
                <span className="inline-block text-xs font-medium tracking-[0.15em] text-primary-600 bg-primary-100/70 px-4 py-1.5 rounded-full mb-6">
                  ACCESS
                </span>
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 tracking-wide">
                  アクセス
                </h2>
              </div>
              <div className="rounded-2xl overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3241.7479754683745!2d139.7292!3d35.6605!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188b770633d17b%3A0x9bc3b7f2648e8e40!2sRoppongi%20Hills%20Mori%20Tower!5e0!3m2!1sen!2sjp!4v1700000000000"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="オフィス所在地"
                  className="w-full"
                ></iframe>
              </div>
              <div className="mt-8 text-center">
                <p className="text-sm text-foreground-600">
                  東京メトロ日比谷線「六本木駅」1C出口より徒歩3分
                </p>
                <p className="text-sm text-foreground-600">
                  都営大江戸線「六本木駅」3番出口より徒歩5分
                </p>
              </div>
            </div>
          </section>
        </>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <section className="py-16 md:py-20">
          <div className="max-w-[1280px] mx-auto px-6 md:px-10">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-12 tracking-wide">
              沿革
            </h2>
            <div className="relative max-w-3xl">
              {/* Timeline line */}
              <div className="absolute left-[15px] top-2 bottom-2 w-px bg-background-300"></div>

              <div className="space-y-10">
                {history.map((item, index) => (
                  <div key={index} className="relative pl-12">
                    {/* Dot */}
                    <div className={`absolute left-[11px] top-1.5 w-[9px] h-[9px] rounded-full border-2 ${
                      index === 0
                        ? 'bg-primary-500 border-primary-500'
                        : 'bg-background-50 border-background-300'
                    }`}></div>
                    <div>
                      <span className="text-xs font-medium text-primary-600 tracking-wider">{item.year}</span>
                      <p className="text-sm text-foreground-700 mt-1.5 leading-relaxed">{item.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 text-center">
          <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-4 tracking-wide">
            データの可能性を、共に
          </h2>
          <p className="text-sm text-foreground-500 mb-8 max-w-lg mx-auto">
            3D's Card Dataにご興味をお持ちいただけましたら、お気軽にお問い合わせください。
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 text-background-50 font-medium text-sm tracking-wider rounded-full hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            お問い合わせ
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}